import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import SocialLoginButton from '../components/common/SocialLoginButton';

const LoginPage: React.FC = () => {
  const { login, loginWithGoogle, loginWithFacebook } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Store redirect path for after login
    const searchParams = new URLSearchParams(location.search);
    const redirectAfterAuth = searchParams.get('redirect') || '/';
    sessionStorage.setItem('redirectAfterAuth', redirectAfterAuth);

    // Check for auth errors from OAuth callback
    const authError = searchParams.get('error');
    if (authError) {
      setError(t('auth.socialLoginError'));
    }
    const blockedErrorCode = sessionStorage.getItem('authErrorCode');
    if (blockedErrorCode === 'BLOCKED_ACCOUNT') {
      sessionStorage.removeItem('authErrorCode');
      setError(t('auth.blockedAccount'));
    }
  }, [location.search, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(formData.email, formData.password, formData.rememberMe);
      
      // Don't redirect here - let the AuthContext handle it after user is set
      // The auth state change listener will handle the redirect
      
    } catch (err) {
      if (err instanceof Error && err.message === 'BLOCKED_ACCOUNT') {
        setError(t('auth.blockedAccount'));
      } else {
        setError(err instanceof Error ? err.message : t('auth.error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'facebook') => {
    setError('');
    setIsLoading(true);

    try {
      if (provider === 'google') {
        await loginWithGoogle();
      } else if (provider === 'facebook') {
        await loginWithFacebook();
      }
      // OAuth redirect will handle the rest
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.socialLoginError'));
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex items-center justify-center mb-8">
          <img
            src="/stanorama-logo.svg"
            alt="Stanorama"
            className="h-12 w-auto"
          />
        </div>
        <h2 className="text-center text-3xl font-extrabold text-neutral-900 mb-2">
          {t('auth.signIn')}
        </h2>
        <p className="text-center text-sm text-neutral-600">
          {t('auth.signInSubtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-soft sm:rounded-2xl sm:px-10 border border-neutral-200">
          {/* Social Login Section - TEMPORARILY DISABLED */}
          {/* <div className="space-y-3">
            <SocialLoginButton
              provider="google"
              onClick={() => handleSocialLogin('google')}
              disabled={isLoading}
            />
            <SocialLoginButton
              provider="facebook"
              onClick={() => handleSocialLogin('facebook')}
              disabled={isLoading}
            />
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-neutral-500">
                  {t('auth.orContinueWith')}
                </span>
              </div>
            </div>
          </div> */}

          {/* Email/Password Form */}
          <form className="space-y-6 mt-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700">
                {t('auth.email')}
              </label>
              <div className="mt-1">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('auth.enterEmail')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-neutral-700">
                {t('auth.password')}
              </label>
              <div className="mt-1">
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('auth.enterPassword')}
                />
              </div>
            </div>

            {/* Remember Me and Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-neutral-700">
                  {t('auth.rememberMe')}
                </label>
              </div>
              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-primary-600 hover:text-primary-500">
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">
                  {error}
                </div>
              </div>
            )}

            <div>
              <Button
                type="submit"
                fullWidth
                disabled={isLoading}
                className="justify-center"
              >
                {isLoading ? t('auth.processing') : t('auth.signIn')}
              </Button>
            </div>
          </form>

          {/* Sign up link */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-neutral-500">
                  {t('auth.noAccount')}
                </span>
              </div>
            </div>

            <div className="mt-6">
              <Link to="/register">
                <Button
                  variant="outline"
                  fullWidth
                  disabled={isLoading}
                  className="justify-center"
                >
                  {t('auth.signUp')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 
